import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { DataGrid } from "@mui/x-data-grid";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import axios from "axios";
import { useAuth } from "context/AuthContext";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";

function StudentCertificates() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState("ALL");
  const [selectedBranch, setSelectedBranch] = useState("ALL");
  const [batches, setBatches] = useState([]);
  const [branches, setBranches] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const fetchBatchesAndBranches = async () => {
      if (!clientId || !collegeId) return;

      try {
        const [batchesResponse, branchesResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/admittedbatches/${clientId}/${collegeId}`, {
            headers: getAuthHeaders(),
          }),
          axios.get(`${API_BASE_URL}/api/branches/${clientId}/${collegeId}`, {
            headers: getAuthHeaders(),
          }),
        ]);

        setBatches(batchesResponse.data.items || batchesResponse.data.data || []);
        setBranches(
          branchesResponse.data.items || branchesResponse.data.data || branchesResponse.data || []
        );
      } catch (error) {
        console.error("Error fetching batches and branches:", error);
        showSnackbar("Failed to load batches and branches", "error");
      }
    };

    fetchBatchesAndBranches();
  }, [clientId, collegeId]);

  useEffect(() => {
    const fetchStudents = async () => {
      if (!clientId || !collegeId) return;

      setLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/students/list/${clientId}/${collegeId}`,
          {
            params: {
              batch: selectedBatch,
              branch: selectedBranch,
            },
            headers: getAuthHeaders(),
          }
        );

        const studentsData = Array.isArray(response.data)
          ? response.data
          : response.data?.data || response.data?.students || [];

        if (studentsData.length > 0) {
          setStudents(studentsData);
        } else {
          console.warn("No students found in API response.");
          setStudents([]);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
        showSnackbar("Failed to load students", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [selectedBatch, selectedBranch, clientId, collegeId]);

  const filteredStudents = useMemo(() => {
    // No need to filter since backend already handles filtering
    return students;
  }, [students]);

  const ActionsMenu = ({ row, showSnackbar: showSnackbarProp }) => {
    const showSnackbar = showSnackbarProp;

    const [anchorEl, setAnchorEl] = useState(null);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [zipDialogOpen, setZipDialogOpen] = useState(false);
    const [certificates, setCertificates] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectedZipFile, setSelectedZipFile] = useState(null);
    const [uploadType, setUploadType] = useState("single"); // Default to single upload
    const [selectedDocType, setSelectedDocType] = useState(""); // For single upload document type
    const fileInputRef = useRef(null);
    const zipFileInputRef = useRef(null);

    const open = Boolean(anchorEl);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleUploadCertificates = () => {
      setUploadDialogOpen(true);
      handleClose();
    };

    const handleFileChange = (event) => {
      const files = Array.from(event.target.files);

      setSelectedFiles(files);
    };

    const handleZipFileChange = (event) => {
      const file = event.target.files[0];

      setSelectedZipFile(file);
    };

    const handleViewCertificates = async () => {
      handleClose();

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/certificates/view/${row.student_id}`,
          {
            params: { clientId },
            headers: getAuthHeaders(),
          }
        );

        setCertificates(response.data.certificates || []);
        setViewDialogOpen(true);
      } catch (error) {
        console.error("Error viewing certificates:", error);
        showSnackbar("Failed to load certificates. Please try again.", "error");
      }
    };

    const handleUpload = async () => {
      if (!clientId) {
        console.error("Client ID is missing. Cannot proceed with upload.");
        showSnackbar("Client ID is missing. Please log in again.", "error");
        return;
      }

      if (selectedFiles.length === 0) {
        console.warn("No files selected for upload.");
        showSnackbar("Please select at least one file.", "error");
        return;
      }

      if (uploadType === "single" && !selectedDocType) {
        showSnackbar("Please select document type for single upload.", "error");
        return;
      }

      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("certificates", file);
      });

      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/certificates/upload/${row.student_id}`,
          formData,
          {
            params: { clientId },
            headers: {
              "Content-Type": "multipart/form-data",
              ...getAuthHeaders(),
            },
          }
        );

        showSnackbar("Certificates uploaded successfully!", "success");
        setUploadDialogOpen(false);
        setSelectedFiles([]);
        // Refresh student list to update certificate count
        window.location.reload();
      } catch (error) {
        console.error("Error uploading certificates:", error);
        const errorMsg =
          error.response?.data?.error || "Failed to upload certificates. Please try again.";
        showSnackbar(errorMsg, "error");
      }
    };

    const handleUploadZip = () => {
      setZipDialogOpen(true);
      handleClose();
    };

    const handleZipUpload = async () => {
      if (!clientId) {
        console.error("Client ID is missing. Cannot proceed with upload.");
        showSnackbar("Client ID is missing. Please log in again.", "error");
        return;
      }

      if (!selectedZipFile) {
        console.warn("No ZIP file selected for upload.");
        showSnackbar("Please select a ZIP file.", "error");
        return;
      }

      const formData = new FormData();
      formData.append("file", selectedZipFile);
      formData.append("studentId", row.student_id);
      formData.append("uploadedBy", user?.user_id || 1);

      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/certificates/upload-zip?clientId=${clientId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              ...getAuthHeaders(),
            },
          }
        );

        const result = response.data;
        if (result.errors && result.errors.length > 0) {
          showSnackbar(
            `Uploaded ${result.successful} files. ${result.failed} failed. Check console for details.`,
            "warning"
          );
        } else {
          showSnackbar(
            `ZIP uploaded successfully! ${result.successful} files processed.`,
            "success"
          );
        }

        setZipDialogOpen(false);
        setSelectedZipFile(null);

        // Refresh the page or student list to show updated certificate count
        window.location.reload();
      } catch (error) {
        console.error("Error uploading ZIP:", error);
        const errorMsg = error.response?.data?.error || "Failed to upload ZIP. Please try again.";
        showSnackbar(errorMsg, "error");
      }
    };

    return (
      <div>
        <IconButton
          aria-label="more"
          aria-controls="actions-menu"
          aria-haspopup="true"
          onClick={handleClick}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu id="actions-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={handleUploadCertificates}>Upload Certificates</MenuItem>
          <MenuItem onClick={handleViewCertificates}>View Certificates</MenuItem>
          <MenuItem onClick={handleUploadZip}>Upload Zip</MenuItem>
        </Menu>

        <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
          <DialogTitle>Upload Certificates</DialogTitle>
          <DialogContent>
            <MDBox mb={2}>
              <MDTypography variant="body2" color="black">
                Upload certificates for {row.student_name} ({row.student_rollno}).
              </MDTypography>
              {uploadType === "single" && (
                <MDTypography variant="caption" color="black" mt={1}>
                  Select the document type and upload the certificate file.
                </MDTypography>
              )}
            </MDBox>
            <RadioGroup
              row
              value={uploadType}
              onChange={(e) => {
                setUploadType(e.target.value);
                setSelectedDocType(""); // Reset doc type when switching
                setSelectedFiles([]); // Reset files when switching
              }}
            >
              <FormControlLabel value="single" control={<Radio />} label="Single Certificate" />
              <FormControlLabel
                value="multiple"
                control={<Radio />}
                label="Multiple Certificates"
              />
            </RadioGroup>

            {uploadType === "single" && (
              <MDBox mb={2}>
                <TextField
                  select
                  size="medium"
                  label="Select Document Type"
                  value={selectedDocType}
                  onChange={(e) => setSelectedDocType(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="">Select Document Type</option>
                  <option value="10th">10th</option>
                  <option value="inter">Inter</option>
                  <option value="diploma">Diploma</option>
                  <option value="degree">Degree</option>
                  <option value="caste">Caste</option>
                  <option value="study">Study</option>
                  <option value="income">Income</option>
                  <option value="tc">TC</option>
                  <option value="pc">PC</option>
                  <option value="od">OD</option>
                </TextField>
              </MDBox>
            )}

            <input
              type="file"
              multiple={uploadType === "multiple"} // Allow multiple files only if "multiple" is selected
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
            />
            <p>Selected Files: {selectedFiles.map((file) => file.name).join(", ")}</p>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={() => setUploadDialogOpen(false)} color="secondary">
              Cancel
            </MDButton>
            <MDButton onClick={handleUpload} color="primary">
              Upload
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog open={zipDialogOpen} onClose={() => setZipDialogOpen(false)}>
          <DialogTitle>Upload ZIP File</DialogTitle>
          <DialogContent>
            <MDBox mb={2}>
              <MDTypography variant="body2" color="black">
                Upload a ZIP file containing multiple certificates for {row.student_name} (
                {row.student_rollno}).
              </MDTypography>
              <MDTypography variant="caption" color="black" mt={1}>
                Files should be named with document type code (e.g., 10th_rollno.pdf,
                inter_rollno.pdf)
              </MDTypography>
            </MDBox>
            <input type="file" ref={zipFileInputRef} onChange={handleZipFileChange} accept=".zip" />
            {selectedZipFile && (
              <MDTypography variant="body2" mt={2}>
                Selected: {selectedZipFile.name} (
                {(selectedZipFile.size / (1024 * 1024)).toFixed(2)} MB)
              </MDTypography>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton onClick={() => setZipDialogOpen(false)} color="secondary">
              Cancel
            </MDButton>
            <MDButton onClick={handleZipUpload} color="primary" disabled={!selectedZipFile}>
              Upload ZIP
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Certificates for {row.student_name} ({row.student_rollno})
          </DialogTitle>
          <DialogContent>
            {certificates.length === 0 ? (
              <MDTypography>No certificates found for this student.</MDTypography>
            ) : (
              <Grid container spacing={2}>
                {certificates.map((cert, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <MDBox p={2}>
                        <MDTypography variant="h6" gutterBottom>
                          {cert.filename}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Size: {(cert.size / 1024).toFixed(2)} KB
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          Uploaded: {new Date(cert.uploadDate).toLocaleDateString()}
                        </MDTypography>
                        <MDBox mt={2}>
                          <MDButton
                            variant="outlined"
                            color="primary"
                            size="medium"
                            onClick={() => window.open(cert.path, "_blank")}
                          >
                            View/Download
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton onClick={() => setViewDialogOpen(false)} color="secondary">
              Close
            </MDButton>
          </DialogActions>
        </Dialog>
      </div>
    );
  };

  ActionsMenu.propTypes = {
    row: PropTypes.shape({
      student_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      student_name: PropTypes.string,
      student_rollno: PropTypes.string,
    }).isRequired,
    showSnackbar: PropTypes.func.isRequired,
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 70,
      renderCell: (params) => {
        const index = filteredStudents.findIndex(
          (row) => row.student_rollno === params.row.student_rollno
        );

        return index + 1 + paginationModel.page * paginationModel.pageSize;
      },
    },
    {
      field: "student_rollno",
      headerName: "Roll Number",
      width: 140,
    },
    {
      field: "branch_name",
      headerName: "Branch",
      width: 180,
      valueGetter: (params) => params.row.branch?.branch_name || "",
    },
    {
      field: "student_name",
      headerName: "Student Name",
      width: 250,
    },
    {
      field: "certificatesCount",
      headerName: "Certificates Count",
      width: 160,
      type: "number",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => <ActionsMenu row={params.row} showSnackbar={showSnackbar} />,
    },
  ];

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={1}
              mt={-3}
              py={2}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h5" color="white">
                MANAGE STUDENT CERTIFICATES
              </MDTypography>
            </MDBox>
            <MDBox pt={3} px={3}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Select Admitted Batch"
                    name="admitted_batch"
                    select
                    fullWidth
                    size="medium"
                    value={selectedBatch}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true, displayEmpty: true }}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                  >
                    <option value="ALL">-- All Batches --</option>
                    {batches.map((batch) => (
                      <option key={batch.admittedbatch_id} value={batch.admittedbatch}>
                        {batch.admittedbatch}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Select Branch"
                    name="branch_id"
                    select
                    fullWidth
                    size="medium"
                    value={selectedBranch}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true, displayEmpty: true }}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                  >
                    <option value="ALL">-- All Branches --</option>
                    {branches.map((branch) => (
                      <option key={branch.branch_id} value={branch.branch_id}>
                        {branch.branch_name}
                      </option>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </MDBox>
            <MDBox pt={3} sx={{ height: 560, ml: 2, mr: 2 }}>
              <DataGrid
                rows={filteredStudents}
                columns={columns}
                loading={loading}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                disableSelectionOnClick
                getRowId={(row) =>
                  String(row.student_id ?? row.student_rollno ?? row.student_name ?? Math.random())
                }
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </MDBox>
  );
}

export default StudentCertificates;
